import React from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { List } from 'immutable';

import { color } from '../../../styles/helpers/variables';

class ProductPageGallery extends React.Component {
  constructor(props) {
    super(props);
    this.galleryThumbnailListRef = React.createRef();
    this.setAsMainImage = this.setAsMainImage.bind(this);
    this.checkScrollArrowVisibility = this.checkScrollArrowVisibility.bind(this);
    this.scrollThumbnails = this.scrollThumbnails.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onThumbnailListScroll = this.onThumbnailListScroll.bind(this);
    this.state = {
      variantId: this.props.variantId,
      mainImage: this.props.images[0] || {},
      thumbnailArrowsVisible: false,
      thumbnailScrolledToTop: true,
      thumbnailScrolledToBottom: false,
      lastTime: 0
    };
  }

  componentDidMount() {
    this.checkScrollArrowVisibility();
    window.addEventListener('resize', this.onResize);
    const galleryThumbnailListRefCurrent = this.galleryThumbnailListRef.current;
    if (galleryThumbnailListRefCurrent) {
      galleryThumbnailListRefCurrent.addEventListener('scroll', this.onThumbnailListScroll);
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.variantId !== prevProps.variantId) {
      this.checkScrollArrowVisibility();
      const galleryThumbnailListRefCurrent = this.galleryThumbnailListRef.current;
      if (galleryThumbnailListRefCurrent && galleryThumbnailListRefCurrent.scrollTop > 0) {
        galleryThumbnailListRefCurrent.scrollTop = 0;
      }
    }
  }

  static getDerivedStateFromProps(props, state) {
    if (props.variantId !== state.variantId) {
      return {
        variantId: props.variantId,
        mainImage: props.images[0] || {}
      };
    }
    return null;
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
    const galleryThumbnailListRefCurrent = this.galleryThumbnailListRef.current;
    if (galleryThumbnailListRefCurrent) {
      galleryThumbnailListRefCurrent.removeEventListener('scroll', this.onThumbnailListScroll);
    }
  }

  onResize() {
    this.checkScrollArrowVisibility();
  }

  onThumbnailListScroll() {
    this.checkScrollArrowVisibility(true);
  }

  setAsMainImage(newMainImageUrl) {
    this.setState({ mainImage: this.props.images.find(image => image.url === newMainImageUrl) });
  }

  checkScrollArrowVisibility(throttle) {
    if (throttle && !(this.state.lastTime + 1 < Date.now())) {
      return;
    }
    const galleryThumbnailListRefCurrent = this.galleryThumbnailListRef.current;
    if (galleryThumbnailListRefCurrent) {
      this.setState({
        thumbnailArrowsVisible:
          galleryThumbnailListRefCurrent.offsetHeight < galleryThumbnailListRefCurrent.scrollHeight,
        thumbnailScrolledToTop: galleryThumbnailListRefCurrent.scrollTop === 0,
        thumbnailScrolledToBottom:
          galleryThumbnailListRefCurrent.scrollTop ===
          galleryThumbnailListRefCurrent.scrollHeight - galleryThumbnailListRefCurrent.offsetHeight,
        lastTime: Date.now()
      });
    }
  }

  scrollThumbnails(direction) {
    const galleryThumbnailListRefCurrent = this.galleryThumbnailListRef.current;
    if (galleryThumbnailListRefCurrent) {
      const distance = galleryThumbnailListRefCurrent.offsetWidth + 20;
      galleryThumbnailListRefCurrent.scrollBy({ top: distance * direction, left: 0, behavior: 'smooth' });
    }
  }

  render() {
    const { images, tags } = this.props;
    const { mainImage, thumbnailArrowsVisible, thumbnailScrolledToTop, thumbnailScrolledToBottom } = this.state;

    if (!images || !images.length || !mainImage) {
      return null;
    }

    return (
      <Gallery>
        <GalleryMain needsBackground={mainImage.needsBackground}>
          <GalleryMainImage src={mainImage.url} alt={mainImage.alt} />
          {tags.size > 0 && (
            <GalleryTagList>
              {tags.map((tag, index) => (
                <GalleryTagListItem key={`gallery-tag-list-item-${tag.get('key')}-${tag.get('value')}-${index}`}>
                  <GalleryTag color={tag.get('key')}>{tag.get('value')}</GalleryTag>
                </GalleryTagListItem>
              ))}
            </GalleryTagList>
          )}
        </GalleryMain>
        <GalleryThumbnails>
          <GalleryThumbnailScrollButton
            type="button"
            title="Scrolla upp"
            direction="up"
            visible={thumbnailArrowsVisible}
            disabled={thumbnailArrowsVisible && thumbnailScrolledToTop}
            onClick={() => this.scrollThumbnails(-1)}
          />
          <GalleryThumbnailList innerRef={this.galleryThumbnailListRef}>
            {images.map((image, index) => (
              <GalleryThumbnailListItem key={`gallery-thumbnail-list-item-${image.url}-${index}`}>
                <GalleryThumbnailButton
                  type="button"
                  active={image.url === mainImage.url}
                  onClick={() => this.setAsMainImage(image.url)}
                >
                  <GalleryThumbnailImage src={image.url} alt={image.alt} needsBackground={image.needsBackground} />
                </GalleryThumbnailButton>
              </GalleryThumbnailListItem>
            ))}
          </GalleryThumbnailList>
          <GalleryThumbnailScrollButton
            type="button"
            title="Scrolla ner"
            direction="down"
            visible={thumbnailArrowsVisible}
            disabled={thumbnailArrowsVisible && thumbnailScrolledToBottom}
            onClick={() => this.scrollThumbnails(1)}
          />
        </GalleryThumbnails>
      </Gallery>
    );
  }
}

ProductPageGallery.defaultProps = {
  images: [],
  tags: List(),
  variantId: ''
};

ProductPageGallery.propTypes = {
  images: PropTypes.array,
  tags: PropTypes.instanceOf(List),
  variantId: PropTypes.string
};

export default ProductPageGallery;

const Gallery = styled.div`
  position: relative;
`;

const GalleryMain = styled.div`
  position: relative;
  width: 83%;
  background-color: ${color.white};
  ${props =>
    !props.needsBackground &&
    css`
      background-color: transparent;
    `};
  @media print {
    width: 100%;
  }
  &:after {
    content: '';
    display: block;
    padding: 0 0 100%;
  }
`;

const GalleryMainImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  object-fit: contain;
`;

const GalleryTagList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  list-style: none;
  position: absolute;
  top: 0;
  left: 0;
  max-width: calc(100% + 5px);
  margin: 0 -5px -5px 0;
  padding: 10px;
`;

const GalleryTagListItem = styled.li`
  min-width: 0;
  padding: 0 5px 5px 0;
`;

const GalleryTag = styled.div`
  padding: 8px 10px;
  background-color: #a5c4c3;
  background-color: ${props => props.color};
  color: ${color.white};
  font-size: 12px;
  font-weight: 600;
  line-height: 0.75;
  letter-spacing: 0.125em;
  text-transform: uppercase;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

export const GalleryThumbnails = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 17.1%;
  padding: 0 0 0 20px;
  @media print {
    display: none;
  }
`;

export const GalleryThumbnailScrollButton = styled.button`
  position: absolute;
  left: 50%;
  margin: 0 0 0 10px;
  outline: none; // …
  border: none;
  padding: 20px;
  background-color: transparent;
  cursor: pointer;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.125s ease, visibility 0s linear 0.125s;
  ${props =>
    props.direction === 'up' &&
    css`
      top: 0;
      transform: translate(-50%, -100%);
    `};
  ${props =>
    props.direction === 'down' &&
    css`
      bottom: 0;
      transform: translate(-50%, 100%);
    `};
  ${props =>
    props.visible &&
    css`
      opacity: 1;
      visibility: visible;
      transition: opacity 0.125s ease, visibility 0s linear 0s;
    `};
  ${props =>
    props.disabled &&
    css`
      opacity: 0.5;
    `};
  &:after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    height: 15px;
    width: 15px;
    background-image: url('/images/icons/arrow.svg');
    background-position: center;
    background-repeat: no-repeat;
    background-size: contain;
    transform: translate(-50%, -50%);
    ${props =>
      props.direction === 'up' &&
      css`
        transform: translate(-50%, -50%) rotate(180deg);
      `};
  }
`;

export const GalleryThumbnailList = styled.ul`
  list-style: none;
  height: 100%;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  scroll-snap-type: y;
`;

export const GalleryThumbnailListItem = styled.li`
  margin: 0 0 20px;
  scroll-snap-align: start;
  &:last-child {
    margin: 0;
  }
`;

export const GalleryThumbnailButton = styled.button`
  display: block;
  position: relative;
  width: 100%;
  padding: 0;
  border: none;
  outline: none;
  background-color: ${color.white};
  cursor: pointer;
  ${props =>
    props.active &&
    css`
    /* border: 2.5px solid ${color.black}; */
  `};
  &:after {
    content: '';
    display: block;
    padding: 0 0 100%;
  }
`;

export const GalleryThumbnailImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  object-fit: contain;
  ${props =>
    !props.needsBackground &&
    css`
      object-fit: cover;
    `};
`;



// WEBPACK FOOTER //
// ./src/components/productPage/ProductPageGallery/ProductPageGallery.js